import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AuthModal from '@/components/AuthModal';
import { Button } from '@/components/ui/button';
import { Package, ShoppingCart, ArrowLeft, Info } from 'lucide-react';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.info('Please login to add items to cart');
      setShowAuthModal(true);
      return;
    }

    try {
      const cartResponse = await axios.get(`${API}/cart`);
      const currentCart = cartResponse.data;
      
      const existingItem = currentCart.items?.find(item => item.product_id === product.id);
      const newItems = existingItem
        ? currentCart.items.map(item => 
            item.product_id === product.id 
              ? { ...item, quantity: item.quantity + quantity }
              : item
          )
        : [...(currentCart.items || []), { product_id: product.id, quantity }];

      await axios.post(`${API}/cart`, { 
        user_email: user.email,
        items: newItems 
      });
      toast.success(`Added ${quantity} case(s) to cart!`);
      navigate('/cart');
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    }
  };

  const calculatePrice = (qty) => {
    if (!product) return 0;
    const subtotal = product.price_per_case * qty;
    let discount = 0;
    if (qty >= 51) discount = 0.10;
    else if (qty >= 11) discount = 0.05;
    return subtotal * (1 - discount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/products')}
          className="mb-6"
          data-testid="back-to-products-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Product Image Gallery */}
            <div>
              {/* Main Image */}
              <div className="bg-white rounded-xl mb-6 flex items-center justify-center overflow-hidden border-2 border-gray-200" style={{ height: '450px' }}>
                {product.images && product.images.length > 0 ? (
                  <img 
                    src={product.images[selectedImage]} 
                    alt={product.name}
                    className="w-full h-full object-contain p-8"
                  />
                ) : product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-contain p-8"
                  />
                ) : (
                  <Package className="h-64 w-64 text-blue-600" />
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {product.images && product.images.length > 1 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Product Views</p>
                  <div className="grid grid-cols-3 gap-3">
                    {product.images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`border-2 rounded-lg overflow-hidden hover:border-blue-600 transition-all ${
                          selectedImage === index ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-300'
                        }`}
                        style={{ height: '100px' }}
                        data-testid={`thumbnail-${index}`}
                      >
                        <img 
                          src={img} 
                          alt={`${product.name} view ${index + 1}`}
                          className="w-full h-full object-contain p-2 bg-white"
                        />
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-center gap-2 mt-2">
                    {product.images.map((_, index) => (
                      <span 
                        key={index}
                        className={`text-xs ${selectedImage === index ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}
                      >
                        {index === 0 ? 'Cup' : index === 1 ? 'Froth' : 'Package'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-4">
                <span className={`badge ${product.design === 'POS Cups™' ? 'badge-blue' : 'badge-gray'}`}>
                  {product.design === 'POS Cups™' ? 'POS Cups\u2122' : product.design}
                </span>
              </div>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="product-name">
                {product.name}
              </h1>
              
              <div className="mb-6">
                <p className="text-4xl font-bold text-blue-600" data-testid="product-price">
                  ${product.price_per_case.toFixed(2)}
                </p>
                <p className="text-gray-500">per case (standard pricing)</p>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Bulk Discount Available</p>
                    <p className="text-sm text-gray-700">11-50 cases: 5% off | 51+ cases: 10% off</p>
                  </div>
                </div>
              </div>

              {/* Specifications */}
              <div className="space-y-4 mb-8">
                <h3 className="text-xl font-bold text-gray-900">Specifications</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Item Code</p>
                    <p className="font-semibold text-gray-900">{product.item_code}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Size</p>
                    <p className="font-semibold text-gray-900">{product.size} oz</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Case Quantity</p>
                    <p className="font-semibold text-gray-900">{product.case_quantity} cups</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Packaging</p>
                    <p className="font-semibold text-gray-900">{product.quantity_detail}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Box Dimensions</p>
                    <p className="font-semibold text-gray-900">{product.box_dimensions}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Case Weight</p>
                    <p className="font-semibold text-gray-900">{product.case_weight}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Cases per Pallet</p>
                    <p className="font-semibold text-gray-900">{product.cases_per_pallet}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">UPC</p>
                    <p className="font-semibold text-gray-900">{product.upc}</p>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Quantity (Cases)
                </label>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="decrease-quantity-btn"
                  >
                    -
                  </Button>
                  <input 
                    type="number" 
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24 text-center border-2 border-gray-300 rounded-lg p-2 font-semibold"
                    min="1"
                    data-testid="quantity-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="increase-quantity-btn"
                  >
                    +
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Total: <span className="font-bold text-gray-900">${calculatePrice(quantity).toFixed(2)}</span>
                  {quantity >= 11 && <span className="text-green-600 ml-2">(Discount applied!)</span>}
                </p>
              </div>

              <Button 
                onClick={handleAddToCart}
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                data-testid="add-to-cart-detail-btn"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add {quantity} Case(s) to Cart
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      )}
    </div>
  );
};

export default ProductDetail;